import React, { FC } from 'react';
import { useTranslate } from 'react-admin';

import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { TextField, StatusField } from '~/components';
import { AsideBase } from '~/layout';
import DeleteAction from './DeleteAction';
import EditAction from './EditAction';
import { time } from '~/utils';
import { AccountExternalLink } from '~/types/AccountExternalLink';
import { isEmpty } from 'lodash';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(1),
    width: '100%',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  editable: {
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[1],
    },
  },
  leftSpacing: {
    marginLeft: theme.spacing(4),
  },
  text: {
    '& *': {
      fontWeight: 400,
    },
  },
  actions: {
    justifyContent: 'right',
  },
}));

interface Props {
  profileId: string;
  profileType: string;
  accountId: string;
  link: AccountExternalLink;
  width?: number;
  permissions?: any;
}

export const AccountExternalLinkCard: FC<Props> = (props) => {
  const { profileId, profileType, accountId, link, width, permissions } = props;

  const translate = useTranslate();
  const t = (key: string): string => translate(`components.AccountExternalLinks.${key}`);

  const classes = useStyles();

  const hasUpdatePermission = permissions?.includes('externalAccountLink.update');
  const hasDeletePermission = permissions?.includes('externalAccountLink.delete');

  const detailsJson =
    typeof link?.identificationDetails?.details === 'string'
      ? JSON.parse(link?.identificationDetails?.details)
      : link?.identificationDetails?.details;

  return (
    <AsideBase width={width} className={classes.root}>
      <Grid container>
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            <TextField label={t('sourceSystem')}>{link.system}</TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label={t('linkType')} textWrap style={{ wordBreak: 'break-all' }}>
              {link.linkType}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label={t('status')}>
              <StatusField status={link.status} />
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField label={t('lastUpdate')}>
              {link.updated && time(link.updated).format('lll')}
            </TextField>
          </Grid>
          {detailsJson?.lastErrorMessage && (
            <Grid item xs={12}>
              <TextField
                label={
                  t('lastError') +
                  (detailsJson.lastErrorTimestamp
                    ? ' ' + time(detailsJson.lastErrorTimestamp).format('lll')
                    : '')
                }
                textWrap
                style={{ wordBreak: 'break-word' }}
              >
                {detailsJson.lastErrorMessage}
              </TextField>
            </Grid>
          )}
          {!isEmpty(link?.identificationDetails?.referenceId) && (
            <>
              {link?.identificationDetails?.referenceId.map((referenceId) => (
                <Grid item xs={12} key={referenceId.name}>
                  <TextField label={referenceId.name}>{referenceId.value}</TextField>
                </Grid>
              ))}
            </>
          )}
        </Grid>
        <Grid container item spacing={2} style={{ marginTop: 8 }} className={classes.actions}>
          {hasUpdatePermission && (
            <Grid item>
              <EditAction
                profileId={profileId}
                profileType={profileType}
                accountId={accountId}
                link={link}
              />
            </Grid>
          )}
          {hasDeletePermission && (
            <Grid item>
              <DeleteAction link={link} accountId={accountId} />
            </Grid>
          )}
        </Grid>
      </Grid>
    </AsideBase>
  );
};

export default AccountExternalLinkCard;
